import { ThemeConfig } from 'antd';

// Types pour les thèmes
export type ThemeId = 'light' | 'dark';

// Interface pour les métadonnées de thème
export interface ThemeMetadata {
	id: ThemeId;
	name: string;
	category: 'light' | 'dark';
	isDefault?: boolean;
}

// Métadonnées des thèmes
export const themeMetadata: Record<ThemeId, ThemeMetadata> = {
	light: {
		id: 'light',
		name: 'theme.themes.light',
		category: 'light'
	},
	dark: {
		id: 'dark',
		name: 'theme.themes.dark',
		category: 'dark',
		isDefault: true
	}
};

// Configuration de base partagée
const baseThemeConfig: Partial<ThemeConfig> = {
	token: {
		// Rayons de bordure
		borderRadius: 6,
		borderRadiusLG: 8,
		borderRadiusSM: 4,
		borderRadiusXS: 2,

		// Espacements
		padding: 16,
		paddingLG: 24,
		paddingSM: 12,
		paddingXS: 8,
		paddingXXS: 4,

		// Marges
		margin: 16,
		marginLG: 24,
		marginSM: 12,
		marginXS: 8,
		marginXXS: 4,

		// Tailles de police
		fontSize: 14,
		fontSizeLG: 16,
		fontSizeSM: 12,
		fontSizeXL: 20,
		fontSizeHeading1: 30,
		fontSizeHeading2: 20,
		fontSizeHeading3: 16,
		fontSizeHeading4: 20,
		fontSizeHeading5: 16,
		

		// Hauteurs de ligne
		lineHeight: 1.5714285714285714,
		lineHeightLG: 1.5,
		lineHeightSM: 1.66,

		// Transitions
		motionDurationFast: '0.1s',
		motionDurationMid: '0.2s',
		motionDurationSlow: '0.3s',

		boxShadow: 'none',
		boxShadowSecondary: 'none'
	},
	components: {
		Button: {
			borderRadius: 6,
			controlHeight: 32,
			controlHeightLG: 40,
			controlHeightSM: 24,
			boxShadow: 'none',
			boxShadowSecondary: 'none',
			primaryShadow: 'none',
			colorBgContainer: '#A6B9C8'
		},
		Input: {
			borderRadius: 6,
			controlHeight: 32,
			controlHeightLG: 40,
			controlHeightSM: 24
		},
		Card: {
			borderRadius: 8
		},
		Modal: {
			borderRadius: 8,
			zIndexPopupBase: 1000
		},
		Table: {
			borderRadius: 6
		},
		// Configuration des z-index pour les composants Ant Design
		Popover: {
			zIndexPopup: 1001
		},
		Tooltip: {
			zIndexPopup: 100
		},
		Layout: {
			headerHeight: 55,
			headerPadding: '0 1rem'
		}
	}
};

// Thème clair (par défaut)
export const lightTheme: ThemeConfig = {
	...baseThemeConfig,
	token: {
		...baseThemeConfig.token,
		// Couleurs principales
		colorPrimary: '#FFBD31',
		colorSuccess: '#52c41a',
		colorWarning: '#faad14',
		colorError: '#ff4d4f',
		colorInfo: '#1890ff',

		// Couleurs de texte
		colorTextBase: '#000000',
		colorText: '#000000d9',
		colorTextSecondary: '#66666655',
		colorTextTertiary: '#AAAAAA',
		colorTextQuaternary: '#00000080',

		colorBorder: '#BBBBBB',
		colorBorderSecondary: '#BBBBBB',
		colorBgContainer: '#f5f5f5 !important',

		// Ombres
		boxShadow: 'none',
		boxShadowSecondary: 'none'
	},
	components: {
		...baseThemeConfig.components,
		Layout: {
			...baseThemeConfig.components?.Layout,
			bodyBg: '#f5f5f5',
			siderBg: '#FFFFFF',
			headerBg: '#e5e5e5'
		},
		Button: {
			...baseThemeConfig.components?.Button,
			colorTextTertiary: '#999999'
		},
		Card: {
			...baseThemeConfig.components?.Card
		},
		Modal: {
			...baseThemeConfig.components?.Modal
		},
		Table: {
			...baseThemeConfig.components?.Table,
			headerBg: '#fafafa'
		},
		Tooltip: {
			...baseThemeConfig.components?.Tooltip,
			colorBgSpotlight: '#AAAAAAEE', // background
			colorTextLightSolid: 'var(--ant-color-text)'
		},
		Tabs: {
			cardBg: 'transparent !important'
		},
		Tag: {
			...baseThemeConfig.components?.Tag,
			defaultBg: 'transparent'
		}
	}
};

// Thème sombre (par défaut)
export const darkTheme: ThemeConfig = {
	...baseThemeConfig,
	token: {
		...baseThemeConfig.token,

		borderRadius: 3,
		borderRadiusLG: 3,
		borderRadiusSM: 3,
		borderRadiusXS: 3,

		// Couleurs principales
		colorPrimary: '#E89611',
		colorSuccess: '#49aa19',
		colorWarning: '#d89614',
		colorError: '#dc4446',
		colorInfo: '#177ddc',

		// Couleurs de fond - Thème principal avec bg #1C2333
		colorBgBase: '#1C2333',
		colorBgContainer: '#2A3441',
		colorBgElevated: '#3A4553',
		colorBgLayout: '#1C2333',

		// Couleurs de texte
		colorTextBase: '#ffffff',
		colorText: '#ffffffd9',
		colorTextSecondary: '#ffffff73',
		colorTextTertiary: '#CCCCCC',
		colorTextQuaternary: '#ffffff26',

		// Couleurs de bordure
		colorBorder: '#4A5568',
		colorBorderSecondary: '#3A4553',

		boxShadow: 'none',
		boxShadowSecondary: 'none'
	},
	components: {
		...baseThemeConfig.components,
		Layout: {
			...baseThemeConfig.components?.Layout,
			headerBg: '#1C2333',
			bodyBg: '#1C2333',
			siderBg: '#1C2333'
		},
		Tooltip: {
			...baseThemeConfig.components?.Tooltip,
			colorBgSpotlight: '#000000C0', // background
			colorTextLightSolid: 'white' // color
		}
	}
};

// Configuration des thèmes par ID
export const themes: Record<ThemeId, ThemeConfig> = {
	light: lightTheme,
	dark: darkTheme
};

// Fonction utilitaire pour obtenir un thème par ID
export const getThemeById = (themeId: ThemeId): ThemeConfig => {
	return themes[themeId] || lightTheme;
};

// Fonction utilitaire pour obtenir les métadonnées d'un thème
export const getThemeMetadata = (themeId: ThemeId): ThemeMetadata => {
	return themeMetadata[themeId] || themeMetadata.light;
};

// Fonction utilitaire pour obtenir tous les thèmes disponibles
export const getAvailableThemes = (): ThemeMetadata[] => {
	return Object.values(themeMetadata);
};

// Fonction utilitaire pour obtenir les thèmes par catégorie
export const getThemesByCategory = (
	category: ThemeMetadata['category']
): ThemeMetadata[] => {
	return Object.values(themeMetadata).filter(
		(theme) => theme.category === category
	);
};

// Fonction utilitaire pour obtenir le thème par défaut
export const getDefaultTheme = (): ThemeId => {
	// Get it from localStorage
	const defaultTheme = localStorage.getItem('theme-id') as ThemeId;
	if (defaultTheme) {
		return defaultTheme;
	}

	return 'light';
};
